import { Environment } from "./Environment";
import { sdkContainerInstance } from '../global/Container';

import { IFeed, IFeedStructureItem } from "../interfaces/IFeed";
import { FetchProxy } from "intraactive-sdk-helper";
import { IEnvironment } from "../interfaces/IEnvironment";

export default class FeedService {
  public static getFeeds(environment: IEnvironment, tenantName: string, token: string, application?: "messages" | "events", instance?: string): Promise<IFeed[]> {
    return new Promise<IFeed[]>((resolve) => {
      if (environment && tenantName && token) {
        let query = new URLSearchParams();
        if (application) {
          query.append("application", application);
        }
        if (instance) {
          query.append("instance", instance);
        }

        const apiUrl = Environment.getEnvironmentForFeedApi(environment);
        new FetchProxy(`${apiUrl}/${tenantName}/feeds?${query}`, {
          method: "GET"
        })
          .withContainer(sdkContainerInstance)
          .withHeader("AuthToken", token)
          .fetch()
          .then((response) => {
            if (!response.ok) {
              resolve(null);
              return;
            }
            return response.json();
          })
          .then((feeds) => {
            resolve(feeds);
          })
          .catch(error => {
            console.log(error);
            resolve([]);
          });
      } else {
        return resolve([]);
      }
    });
  }

  public static getFeed(environment: IEnvironment, tenantName: string, token: string, id: string): Promise<IFeed> {
    return new Promise<IFeed>((resolve) => {
      if (environment && tenantName && token && id) {
        const apiUrl = Environment.getEnvironmentForFeedApi(environment);
        new FetchProxy(`${apiUrl}/${tenantName}/feeds?id=${id}`, {
          method: "GET"
        })
          .withContainer(sdkContainerInstance)
          .withHeader("AuthToken", token)
          .fetch()
          .then((response) => {
            if (!response.ok) {
              resolve(null);
              return;
            }
            return response.json();
          })
          .then((feeds: IFeed[]) => {
            if (feeds.length != 1) {
              resolve(null);
              return;
            }
            resolve(feeds[0]);
          })
          .catch(error => {
            console.log(error);
            resolve(null);
          });
      } else {
        return resolve(null);
      }
    });
  }

  public static createFeed(environment: IEnvironment, tenantName: string, token: string, feed: IFeed): Promise<IFeed> {
    return new Promise<IFeed>((resolve) => {
      if (environment && tenantName && token && feed) {
        const apiUrl = Environment.getEnvironmentForFeedApi(environment);
        new FetchProxy(`${apiUrl}/${tenantName}/feeds`, {
          method: "POST",
          body: JSON.stringify(feed)
        })
          .withContainer(sdkContainerInstance)
          .withHeader("AuthToken", token)
          .fetch()
          .then((response) => {
            if (!response.ok) {
              resolve(null);
              return;
            }
            return response.json();
          })
          .then((feed) => {
            resolve(feed);
          })
          .catch(error => {
            console.log(error);
            resolve(undefined);
          });
      } else {
        return resolve(undefined);
      }
    });
  }

  public static updateFeed(environment: IEnvironment, tenantName: string, token: string, feed: IFeed): Promise<IFeed> {
    return new Promise<IFeed>((resolve) => {
      if (environment && tenantName && token && feed) {
        const apiUrl = Environment.getEnvironmentForFeedApi(environment);
        new FetchProxy(`${apiUrl}/${tenantName}/feeds/${feed.id}`, {
          method: "PUT",
          body: JSON.stringify(feed)
        })
          .withContainer(sdkContainerInstance)
          .withHeader("AuthToken", token)
          .fetch()
          .then((response) => {
            if (!response.ok) {
              resolve(null);
              return;
            }
            return response.json();
          })
          .then((feed) => {
            resolve(feed);
          })
          .catch(error => {
            console.log(error);
            resolve(undefined);
          });
      } else {
        return resolve(undefined);
      }
    });
  }

  public static deleteFeed(environment: IEnvironment, tenantName: string, token: string, id: string): Promise<void> {
    return new Promise<void>((resolve) => {
      if (environment && tenantName && token && id) {
        const apiUrl = Environment.getEnvironmentForFeedApi(environment);
        new FetchProxy(`${apiUrl}/${tenantName}/feeds/${id}`, {
          method: "DELETE",
        })
          .withContainer(sdkContainerInstance)
          .withHeader("AuthToken", token)
          .fetch()
          .then((response) => {
            if (!response.ok) {
              resolve();
              return;
            }
            return response.json();
          })
          .then(() => {
            resolve();
          })
          .catch(error => {
            console.log(error);
            resolve(undefined);
          });
      } else {
        return resolve(undefined);
      }
    });
  }

  public static generateApiKey(environment: IEnvironment, tenantName: string, token: string) {
    return new Promise<IFeed>((resolve) => {
      if (environment && tenantName && token) {
        const apiUrl = Environment.getEnvironmentForFeedApi(environment);
        new FetchProxy(`${apiUrl}/${tenantName}/keys`, {
          method: "POST",
        })
          .withContainer(sdkContainerInstance)
          .withHeader("AuthToken", token)
          .fetch()
          .then((response) => {
            if (!response.ok) {
              resolve(null);
              return;
            }
            return response.json();
          })
          .then((result) => {
            resolve(result);
          })
          .catch(error => {
            console.log(error);
            resolve(undefined);
          });
      } else {
        return resolve(undefined);
      }
    });
  }

  public static getFeedStructure(environment: IEnvironment, tenantName: string, application: "messages" | "events") {
    return new Promise<IFeedStructureItem[]>((resolve, reject) => {
      if (environment && tenantName && application) {
        const apiUrl = Environment.getEnvironmentForFeedApi(environment);
        new FetchProxy(`${apiUrl}/${tenantName}/${application}/feed-structure`, {
          method: "GET",
        })
          .withContainer(sdkContainerInstance)
          .fetch()
          .then((response) => {
            if (!response.ok) {
              reject(response.status);
              return;
            }
            return response.json();
          })
          .then((result: { structure: IFeedStructureItem[] }) => {
            resolve(result.structure);
          })
          .catch(error => {
            console.log(error);
            return (undefined);
          });
      } else {
        return (undefined);
      }
    });
  }

  public static createFeedStructure(environment: IEnvironment, tenantName: string, application: "messages" | "events", structure: IFeedStructureItem[]) {
    return new Promise<IFeedStructureItem[]>((resolve, reject) => {
      if (environment && tenantName && application && structure) {
        const apiUrl = Environment.getEnvironmentForFeedApi(environment);
        new FetchProxy(`${apiUrl}/${tenantName}/${application}/feed-structure`, {
          method: "POST",
          body: JSON.stringify({ structure })
        })
          .withContainer(sdkContainerInstance)
          .fetch()
          .then((response) => {
            if (!response.ok) {
              reject(response.status);
              return;
            }
            return response.json();
          })
          .then((result: { structure: IFeedStructureItem[] }) => {
            resolve(result.structure);
          })
          .catch(error => {
            reject(error);
          });
      } else {
        return reject(undefined);
      }
    });
  }

  public static updateFeedStructure(environment: IEnvironment, tenantName: string, application: "messages" | "events", structure: IFeedStructureItem[]) {
    return new Promise<IFeedStructureItem[]>((resolve, reject) => {
      if (environment && tenantName && application && structure) {
        const apiUrl = Environment.getEnvironmentForFeedApi(environment);
        new FetchProxy(`${apiUrl}/${tenantName}/${application}/feed-structure`, {
          method: "PUT",
          body: JSON.stringify({ structure })
        })
          .withContainer(sdkContainerInstance)
          .fetch()
          .then((response) => {
            if (!response.ok) {
              reject(response.status);
              return;
            }
            return response.json();
          })
          .then((result: { structure: IFeedStructureItem[] }) => {
            resolve(result.structure);
          })
          .catch(error => {
            reject(error);
          });
      } else {
        return reject(undefined);
      }
    });
  }
}